import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AvatarEditor from 'react-avatar-editor'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/_modal'
import Modal, { ModalBottom, EditModalHeader } from '../mini/Modal'
import { updateAccount } from '../../actions/account'
import { H2, Caption5, H5 } from '../styled/typography/typography'
import FlexAlignCenter from '../styled/FlexAlignCenter'
import { useTheme } from '@emotion/react'
import { dataUriToBlob } from '../../utils/file'
import LoadingSpinner from '../mini/LoadingSpinner'
import UserAvatar from '../mini/UserAvatar'
import BtnC from '../styled/buttons/BtnC'
import BtnA from '../styled/buttons/BtnA'

const sizeLimit = 500000

const AvatarEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 16px 0 8px;

  input {
    margin: 0 0 -8px;
  }
`

const FileName = styled(H5)`
  flex-shrink: 2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RemoveButton = styled(H5).attrs({ as: 'button' })`
  color: ${colors.crimson};
  border: none;
  background: transparent;
`

const SizeWarning = styled.span`
  color: ${props => props.tooBig ? colors.crimson : 'inherit'};
`

const EditAvatar = ({ apiData }) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const user = useSelector(state => state.auth.user)
  const isEditingAccount = useSelector(state => state.auth.isEditingAccount)

  const [fileUrl, setFileUrl] = useState()
  const [fileName, setFileName] = useState()
  const [fileType, setFileType] = useState()
  const [scale, setScale] = useState(1)
  const [editorState, setEditorState] = useState('')
  const [isTooBig, setIsTooBig] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const inputRef = useRef(null)

  const setEditorRef = editor => {
    if (editor) {
      setEditorState(editor)
    }
  }

  const onFileLoad = e => {
    const reader = new FileReader()
    if (e.target.files[0].size < sizeLimit) {
      const name = e.target.value.replace(/^.*[\\\/]/, '')
      reader.onload = e => {
        setFileName(name)
        setFileUrl(e.target.result)
      }
      setFileType(e.target.files[0].type)
      setIsTooBig(false)

      reader.readAsDataURL(e.target.files[0])
    } else {
      handleRemove()
      setIsTooBig(true)
    }
  }

  const onZoom = e => {
    setScale(e.target.value)
  }

  const closeTheModal = () => {
    setTimeout(() => {
      dispatch(hideModal())
    }, 300)
  }

  const isUploaded = !!fileUrl
  const couldSaveData = isUploaded && !isTooBig && !isLoading

  const handleSave = async () => {
    if (couldSaveData) {
      setIsLoading(true)
      // get uri of the edited image
      const canvasScaled = editorState.getImageScaledToCanvas()
      const dataUri = canvasScaled.toDataURL()
      // convert uri to File
      const blob = dataUriToBlob(dataUri)
      const file = new File([blob], fileName, { type: fileType })
      
      dispatch(updateAccount(file, null))
      setShouldClose(true)
    }
  }

  useEffect(() => {
    if (shouldClose && !isEditingAccount) {
      closeTheModal()
    }
  }, [shouldClose, isEditingAccount])

  const handleRemove = () => {
    setFileUrl(null)
    setFileName(null)
    setFileType(null)
    setScale(1)
    setIsTooBig(false)
    setEditorState('')
    document.getElementById('file-input').value = ''
  }

  const displayAvatarCropper = () => (
    <AvatarEditorWrapper>
      {isUploaded ? (
        <>
          <AvatarEditor
            ref={setEditorRef}
            image={fileUrl}
            width={200}
            height={200}
            borderRadius={30}
            border={0}
            scale={scale}
          />
          <input
            className="zoom-slider"
            type="range"
            step="0.01"
            min="1"
            max="2"
            name="scale"
            defaultValue="1"
            onChange={onZoom}
            style={{ width: '200px' }}
          />
        </>
      ) : (
        <UserAvatar
          photoUrl={user.photo.url}
          size={200}
          borderRadius={30}
        />
      )}
    </AvatarEditorWrapper>
  )

  return (
    <Modal
      width={660}
      allowCloseOnExternalClick={false}
      closeCallback={closeTheModal}
    >
      <EditModalHeader
        title="Edit Picture"
        onCancel={closeTheModal}
        onSave={handleSave}
        canCancel={!isLoading}
        canSave={couldSaveData}
      />

      <ModalBottom>
        {isLoading ? (
          <LoadingSpinner isSmall />
        ) : (
          <>
            <div>
              <FlexAlignCenter spaced>
                <BtnC as="label" htmlFor="file-input">
                  Choose File
                </BtnC>
                <input
                  id="file-input"
                  className="activity-icon-input"
                  type="file"
                  accept="image/*"
                  onChange={onFileLoad}
                  ref={inputRef}
                  style={{ display: 'none' }}
                />

                {isUploaded && (
                  <>
                    <FileName>{fileName}</FileName>
                    <RemoveButton onClick={handleRemove}>
                      Remove
                    </RemoveButton>
                  </>
                )}
              </FlexAlignCenter>
              {displayAvatarCropper()}
            </div>
            <Caption5>
              <SizeWarning tooBig={isTooBig}>
                File size limit is 500 KB.
              </SizeWarning>
              &nbsp;
              For best viewing, a square png or jpeg image is recommended.
            </Caption5>
          </>
        )}
      </ModalBottom>
    </Modal>
  )
}

EditAvatar.propTypes = {
  apiData: PropTypes.object.isRequired
}

export default withApiData(EditAvatar)
